<script setup lang="ts">
import { CtButton } from '@camelot/components'
import DateAndTimeSelection from '~/components/input/date-time/DateAndTimeSelection.vue'
import LocationSelection from '~/components/input/location/LocationSelection.vue'
import { CITIES_RECORD } from '~/composables/city'
import { toRentalInstant } from '~/composables/prebooking-date'
import { isRunningOnIntHost } from '~/composables/tracking/tracking-scripts'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useDateTimeStore } from '~/stores/datetime'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'

const locationStore = useLocationStore()
const dateTimeStore = useDateTimeStore()
const offerStore = useOfferStore()

function getBaseUrl(): string {
  return isRunningOnIntHost() ? useRuntimeConfig().public.INTEGRATION_PAGE : useRuntimeConfig().public.MAIN_PAGE
}

// Initialize city from URL parameter if available
onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const cityParam = urlParams.get('city')?.toLowerCase()

  if (cityParam && CITIES_RECORD[cityParam]) {
    locationStore.city = CITIES_RECORD[cityParam]
  }
})

watch(() => [dateTimeStore.errorStartingDate, dateTimeStore.errorEndingDate, dateTimeStore.errorEndingDate], () => {
  window.parent.postMessage('prebookingDateTimeError', '*')
})
const bookingReferralURL = computed<string>(() => {
  // Get current language from URL or use the language from translations
  const { language } = useTranslations()
  const route = useRoute()
  const currentLang = route.params._lang || language.value || 'en'

  // Referral endpoint doesn't need language in path
  const baseUrl = new URL(`${getBaseUrl()}/rental/booking/referral`)
  // Emit the real instant: the referral page reads it back through createDate, which restores
  // the same rental wall-clock for whoever opens the link.
  const startDate = dateTimeStore.startDate.date === undefined ? '' : toRentalInstant(dateTimeStore.startDate.date).toISOString()
  const endDate = dateTimeStore.endDate.date === undefined ? '' : toRentalInstant(dateTimeStore.endDate.date).toISOString()
  const address = locationStore.address?.info === undefined ? '' : (locationStore.address?.info)
  const lat = locationStore.address?.location?.latitude
  const lon = locationStore.address?.location?.longitude
  const city = locationStore.city.name

  baseUrl.searchParams.set('address', address)
  baseUrl.searchParams.set('city', city.toLowerCase())
  baseUrl.searchParams.set('utm_source', 'website')
  baseUrl.searchParams.set('utm_medium', 'widget')
  baseUrl.searchParams.set('lat', String(lat))
  baseUrl.searchParams.set('lon', String(lon))
  baseUrl.searchParams.set('locale', currentLang.toString())

  // need to format dates like this cause searchParams causes formatting issues
  return `${baseUrl}&startDate=${startDate}&endDate=${endDate}`
})

function handleRedirect() {
  navigateTo(bookingReferralURL.value, { external: true, open: { target: '_blank' } })
}
</script>

<template>
  <form class="gap-y-[32px] gap-x-[10px] ct-card flex flex-col xl:flex-row flex-wrap pb-64 pt-44 xs:px-[22px] ">
    <LocationSelection class="xl:w-[40%] md:min-w-[460px] xl:ml-16" />

    <DateAndTimeSelection class="xl:w-5/12 md:min-w-[530px]" />

    <CtButton
      v-if="!offerStore.possibleOffers?.length"
      :disabled="!locationStore.isValid || !dateTimeStore.areDatesValid"
      class="xl:mt-[72px] xl:mr-auto mt-16"
      @click="handleRedirect()"
    >
      {{ translate('prebook_search_button') }}
    </CtButton>
  </form>
</template>

<style scoped lang="scss">
@media screen and (min-width: 1200px) {
  form {
    height: 350px !important;
  }
}
</style>
